// views/adminEstablissement/ManageContainerCardsPage.js

import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Card, CardActions, CardContent, CardMedia, Grid, makeStyles, Typography, Modal, TextField, Select, InputLabel, MenuItem, FormControl, Checkbox, ListItemText, FormControlLabel, Switch, Divider, Chip, Box, Fade, Zoom, Slide } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CreateIcon from '@material-ui/icons/Create';
import DeleteIcon from '@material-ui/icons/Delete';
import StorefrontIcon from '@material-ui/icons/Storefront';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import Navbar from '../../components/Navbar';
import axiosInstance from '../../services/axiosInstance';

const useStyles = makeStyles(theme => ({
    '@global': {
        '@import': [
            'url(https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Manrope:wght@400;500;600;700;800&display=swap)'
        ],
        '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-10px)' }
        },
        '@keyframes shimmer': {
            '0%': { backgroundPosition: '-1000px 0' },
            '100%': { backgroundPosition: '1000px 0' }
        },
        '@keyframes fadeInUp': {
            from: {
                opacity: 0,
                transform: 'translateY(30px)'
            },
            to: {
                opacity: 1,
                transform: 'translateY(0)'
            }
        },
        '@keyframes gradientShift': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' }
        }
    },
    page: {
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
        fontFamily: "'Inter', sans-serif",
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(236, 72, 153, 0.03) 0%, transparent 50%)',
            pointerEvents: 'none',
            zIndex: 0
        }
    },
    header: {
        padding: theme.spacing(5, 4),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        zIndex: 1,
        animation: 'fadeInUp 0.6s ease-out',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: theme.spacing(3),
            alignItems: 'flex-start'
        }
    },
    headerTitle: {
        fontFamily: "'Manrope', sans-serif",
        fontWeight: 800,
        fontSize: '2.75rem',
        background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        letterSpacing: '-0.03em',
        marginBottom: theme.spacing(1)
    },
    headerSubtitle: {
        color: '#64748b',
        fontSize: '1.05rem',
        fontWeight: 500,
        lineHeight: 1.7,
        maxWidth: '650px'
    },
    addButton: {
        textTransform: 'none',
        fontWeight: 700,
        fontSize: '0.95rem',
        borderRadius: '12px',
        padding: theme.spacing(1.5, 3.5),
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white',
        border: 'none',
        boxShadow: '0 4px 14px rgba(99, 102, 241, 0.25)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.35)',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)'
        },
        '&:active': {
            transform: 'translateY(0px)'
        }
    },
    divider: {
        backgroundColor: '#e2e8f0',
        margin: theme.spacing(0, 4),
        position: 'relative',
        zIndex: 1
    },
    establishmentsWrapper: {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4),
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        position: 'relative',
        zIndex: 1,
        animation: 'fadeInUp 0.6s ease-out 0.1s backwards'
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(3)
    },
    sectionTitle: {
        fontFamily: "'Manrope', sans-serif",
        fontWeight: 700,
        fontSize: '1.85rem',
        color: '#1e293b',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5)
    },
    chip: {
        background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
        color: '#7c3aed',
        fontWeight: 600,
        border: '1px solid #c4b5fd',
        fontSize: '0.875rem',
        height: '32px'
    },
    card: {
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.08), 0 10px 20px rgba(0, 0, 0, 0.04)',
            borderColor: '#cbd5e1',
        }
    },
    establishmentCard: {
        minHeight: '200px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
    },
    cardContent: {
        padding: theme.spacing(3)
    },
    establishmentTitle: {
        fontWeight: 700,
        fontSize: '1.3rem',
        color: '#0f172a',
        marginBottom: theme.spacing(1)
    },
    establishmentText: {
        color: '#64748b',
        fontSize: '0.95rem',
        marginBottom: theme.spacing(0.5)
    },
    manageButton: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: '10px',
        padding: theme.spacing(1.2, 2.5),
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        color: '#0284c7',
        border: '1px solid #bae6fd',
        transition: 'all 0.3s ease',
        '&:hover': {
            background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
            borderColor: '#7dd3fc',
            transform: 'translateX(4px)'
        }
    },
    productCard: {
        background: 'white',
        minHeight: '460px',
        display: 'flex',
        flexDirection: 'column'
    },
    productMedia: {
        height: 240,
        borderRadius: '12px',
        margin: theme.spacing(2, 2, 0),
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid #f1f5f9'
    },
    productDetails: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2.5)
    },
    productTitle: {
        fontWeight: 700,
        fontSize: '1.35rem',
        color: '#0f172a',
        marginBottom: theme.spacing(0.5)
    },
    productDescription: {
        color: '#64748b',
        fontSize: '0.95rem',
        lineHeight: 1.6,
        flex: 1
    },
    priceChip: {
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        color: '#92400e',
        fontWeight: 700,
        fontSize: '1rem',
        height: '36px',
        border: '1px solid #fcd34d',
        alignSelf: 'flex-start'
    },
    cardActions: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: theme.spacing(0, 2.5, 2.5),
        gap: theme.spacing(1.5)
    },
    editButton: {
        flex: 1,
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: '10px',
        padding: theme.spacing(1.2, 0),
        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
        color: 'white',
        border: 'none',
        transition: 'all 0.3s ease',
        '&:hover': {
            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(59, 130, 246, 0.3)'
        }
    },
    deleteButton: {
        flex: 1,
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: '10px',
        padding: theme.spacing(1.2, 0),
        background: '#fef2f2',
        color: '#dc2626',
        border: '1px solid #fecaca',
        transition: 'all 0.3s ease',
        '&:hover': {
            background: '#fee2e2',
            borderColor: '#fca5a5',
            transform: 'translateY(-2px)'
        }
    },
    modal: {
        width: '90%',
        maxWidth: 520,
        maxHeight: '90vh',
        overflowY: 'auto',
        background: 'white',
        padding: theme.spacing(4, 4.5),
        borderRadius: '20px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.1), 0 10px 20px rgba(0, 0, 0, 0.05)',
        outline: 'none',
        '&::-webkit-scrollbar': {
            width: '8px'
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f5f9',
            borderRadius: '4px'
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#cbd5e1',
            borderRadius: '4px',
            '&:hover': {
                background: '#94a3b8'
            }
        }
    },
    modalTitle: {
        fontFamily: "'Manrope', sans-serif",
        fontWeight: 800,
        fontSize: '1.75rem',
        color: '#0f172a',
        marginBottom: theme.spacing(3),
        textAlign: 'center'
    },
    fileInput: {
        display: 'none'
    },
    fileInputLabel: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(3),
        border: '2px dashed #cbd5e1',
        borderRadius: '12px',
        background: '#f8fafc',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginBottom: theme.spacing(2),
        '&:hover': {
            borderColor: '#94a3b8',
            background: '#f1f5f9'
        }
    },
    fileInputText: {
        color: '#475569',
        fontWeight: 600,
        fontSize: '0.95rem'
    },
    imagePreview: {
        width: '100%',
        height: 180,
        objectFit: 'cover',
        borderRadius: '12px',
        marginBottom: theme.spacing(2),
        border: '1px solid #e2e8f0'
    },
    textField: {
        marginBottom: theme.spacing(2.5),
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            background: '#f8fafc',
            color: '#0f172a',
            fontFamily: "'Inter', sans-serif",
            '& fieldset': {
                borderColor: '#e2e8f0'
            },
            '&:hover fieldset': {
                borderColor: '#cbd5e1'
            },
            '&.Mui-focused fieldset': {
                borderColor: '#6366f1',
                borderWidth: '2px'
            }
        },
        '& .MuiInputLabel-root': {
            color: '#64748b',
            fontFamily: "'Inter', sans-serif",
            '&.Mui-focused': {
                color: '#6366f1'
            }
        },
        '& .MuiOutlinedInput-input': {
            padding: theme.spacing(1.8, 2)
        }
    },
    selectField: {
        marginBottom: theme.spacing(2.5),
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            background: '#f8fafc',
            color: '#0f172a',
            '& fieldset': {
                borderColor: '#e2e8f0'
            },
            '&:hover fieldset': {
                borderColor: '#cbd5e1'
            },
            '&.Mui-focused fieldset': {
                borderColor: '#6366f1',
                borderWidth: '2px'
            }
        },
        '& .MuiInputLabel-root': {
            color: '#64748b',
            '&.Mui-focused': {
                color: '#6366f1'
            }
        }
    },
    switchControl: {
        marginBottom: theme.spacing(2.5),
        '& .MuiFormControlLabel-label': {
            color: '#334155',
            fontWeight: 500,
            fontFamily: "'Inter', sans-serif"
        },
        '& .MuiSwitch-colorPrimary.Mui-checked': {
            color: '#6366f1'
        },
        '& .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#6366f1'
        }
    },
    modalButton: {
        textTransform: 'none',
        fontWeight: 700,
        fontSize: '1rem',
        borderRadius: '12px',
        padding: theme.spacing(1.8, 3),
        marginTop: theme.spacing(2),
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    saveButton: {
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white',
        border: 'none',
        boxShadow: '0 4px 14px rgba(99, 102, 241, 0.25)',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.35)',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)'
        }
    },
    deleteConfirmButton: {
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        color: 'white',
        border: 'none',
        boxShadow: '0 4px 14px rgba(239, 68, 68, 0.25)',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(239, 68, 68, 0.35)'
        }
    },
    cancelButton: {
        background: '#f1f5f9',
        color: '#475569',
        border: '1px solid #e2e8f0',
        '&:hover': {
            background: '#e2e8f0',
            borderColor: '#cbd5e1'
        }
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(8, 2),
        color: '#94a3b8',
        fontSize: '1.1rem',
        fontWeight: 500
    },
    iconWrapper: {
        width: 48,
        height: 48,
        borderRadius: '12px',
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: theme.spacing(2),
        '& svg': {
            fontSize: '1.75rem',
            color: '#d97706'
        }
    }
}));

function DeleteConfirmationModal({ open, onClose, onConfirm, containerCardId }) {
    const classes = useStyles();

    const handleConfirmClick = () => {
        onConfirm(containerCardId);
    };

    return (
        <Modal 
            open={open} 
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <Zoom in={open}>
                <div className={classes.modal}>
                    <Box display="flex" justifyContent="center" mb={2}>
                        <div className={classes.iconWrapper}>
                            <DeleteIcon />
                        </div>
                    </Box>
                    <Typography className={classes.modalTitle}>
                        Supprimer ce produit ?
                    </Typography>
                    <Typography align="center" style={{ color: '#64748b', marginBottom: '2rem', fontSize: '0.95rem' }}>
                        Cette action est irréversible. Toutes les données associées seront définitivement supprimées.
                    </Typography>
                    <Box display="flex" gap={2}>
                        <Button 
                            variant="contained" 
                            onClick={onClose} 
                            fullWidth 
                            className={`${classes.modalButton} ${classes.cancelButton}`}
                        >
                            Annuler
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={handleConfirmClick} 
                            fullWidth 
                            className={`${classes.modalButton} ${classes.deleteConfirmButton}`}
                        >
                            Supprimer
                        </Button>
                    </Box>
                </div>
            </Zoom>
        </Modal>
    );
}

function ManageProductShop() {
    const classes = useStyles();
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [containerCardToDelete, setContainerCardToDelete] = useState(null);
    const [containerCards, setContainerCards] = useState([]);
    const [establishments, setEstablishments] = useState([]);
    const [availableContainers, setAvailableContainers] = useState([]);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [updateModalOpen, setUpdateModalOpen] = useState(false);
    const [selectedContainerCard, setSelectedContainerCard] = useState(null);

    useEffect(() => {
        axiosInstance.get('/admin/establishments')
            .then(response => {
                const payload = response.data;
                const establishmentList = Array.isArray(payload?.establishments)
                    ? payload.establishments
                    : Array.isArray(payload)
                        ? payload
                        : [];
                setEstablishments(establishmentList);
            })
            .catch(error => console.error('Error fetching establishments: ', error));

        axiosInstance.get('/shop/product-containers')
            .then(response => {
                setAvailableContainers(Array.isArray(response.data) ? response.data : []);
            })
            .catch(error => console.error('Error fetching containers: ', error));
    }, []);

    const fetchContainerCards = () => {
        axiosInstance.get('/shop/products')
            .then(response => {
                setContainerCards(response.data);
            })
            .catch(error => {
                console.error('Error fetching data: ', error);
            });
    };

    useEffect(fetchContainerCards, []);

    const handleAddClick = () => {
        setAddModalOpen(true);
    };

    const handleUpdateClick = (containerCard) => {
        setSelectedContainerCard(containerCard);
        setUpdateModalOpen(true);
    };

    const handleAddContainerCard = (containerCard, creationOptions = {}) => {
        const formData = new FormData();
        formData.append('name', containerCard.name);
        formData.append('description', containerCard.description);
        if (containerCard.price !== '' && containerCard.price !== null && containerCard.price !== undefined) {
            formData.append('price', containerCard.price);
        }
        if (containerCard.container_deposit !== '' && containerCard.container_deposit !== undefined) {
            formData.append('container_deposit', containerCard.container_deposit);
        }
        if (containerCard.container_quantity !== undefined) {
            formData.append('container_quantity', containerCard.container_quantity);
        }
        if (containerCard.container_id) {
            formData.append('container_id', containerCard.container_id);
        }
        if (containerCard.image) {
            formData.append('file', containerCard.image);
        }

        axiosInstance.post('/shop/products', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
            .then(response => {
                const createdProduct = response.data?.container;
                if (createdProduct && (creationOptions.establishmentIds || []).length > 0) {
                    const payloads = creationOptions.establishmentIds.map((establishmentId) =>
                        axiosInstance.post('/shop/establishment/add_product', {
                            establishment_id: establishmentId,
                            productshop_id: createdProduct.id,
                            nb_stock: creationOptions.defaultStock || 0,
                            is_hidden: false,
                            delivery_threshold: creationOptions.deliveryThreshold || 0,
                            progress_enabled: creationOptions.progressEnabled,
                            current_orders: 0
                        })
                    );

                    Promise.all(payloads)
                        .then(() => fetchContainerCards())
                        .catch(error => console.error('Error linking product to establishments: ', error))
                        .finally(() => setAddModalOpen(false));
                } else {
                    fetchContainerCards();
                    setAddModalOpen(false);
                }
            })
            .catch(error => {
                console.error('Error adding data: ', error);
            });
    };

    const handleUpdateContainerCard = (updatedContainerCard, updateOptions = {}) => {
        const formData = new FormData();
        formData.append('name', updatedContainerCard.name);
        formData.append('description', updatedContainerCard.description);
        if (updatedContainerCard.price !== '' && updatedContainerCard.price !== null && updatedContainerCard.price !== undefined) {
            formData.append('price', updatedContainerCard.price);
        }
        if (updatedContainerCard.container_deposit !== '' && updatedContainerCard.container_deposit !== undefined) {
            formData.append('container_deposit', updatedContainerCard.container_deposit);
        }
        if (updatedContainerCard.container_quantity !== undefined) {
            formData.append('container_quantity', updatedContainerCard.container_quantity);
        }
        if (updatedContainerCard.container_id) {
            formData.append('container_id', updatedContainerCard.container_id);
        }
        if (updatedContainerCard.image) {
            formData.append('file', updatedContainerCard.image);
        }

        axiosInstance.put(`/shop/products/${updatedContainerCard.id}`, formData)
            .then(response => {
                console.log(response);
                if ((updateOptions.establishmentIds || []).length > 0) {
                    const payloads = updateOptions.establishmentIds.map((establishmentId) =>
                        axiosInstance.post('/shop/establishment/add_product', {
                            establishment_id: establishmentId,
                            productshop_id: updatedContainerCard.id,
                            nb_stock: updateOptions.defaultStock || 0,
                            is_hidden: false,
                            delivery_threshold: updateOptions.deliveryThreshold || 0,
                            progress_enabled: updateOptions.progressEnabled,
                            current_orders: 0
                        })
                    );
                    return Promise.all(payloads);
                }
            })
            .then(() => {
                fetchContainerCards();
                setUpdateModalOpen(false);
            })
            .catch(error => {
                console.error('Error updating data: ', error);
            });
    };

    const handleDeleteClick = (containerCardId) => {
        setContainerCardToDelete(containerCardId);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirmClick = (containerCardId) => {
        if (!containerCardId) return;

        axiosInstance.delete(`/shop/products/${containerCardId}`)
            .then(response => {
                setContainerCards(prevCards => prevCards.filter(card => card.id !== containerCardId));
                setDeleteModalOpen(false);
                setContainerCardToDelete(null);
            })
            .catch(error => {
                console.error('Error deleting data: ', error);
            });
    };

    return (
        <div className={classes.page}>
            <Navbar />
            <div className={classes.header}>
                <div>
                    <Typography className={classes.headerTitle}>
                        Catalogue boutique
                    </Typography>
                    <Typography className={classes.headerSubtitle}>
                        Ajoutez de nouveaux produits, fixez les stocks par point de vente et gérez les photos plus facilement.
                    </Typography>
                </div>
                <Button
                    variant="contained"
                    className={classes.addButton}
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                >
                    Ajouter un produit
                </Button>
            </div>

            <Divider className={classes.divider} />

            <div className={classes.establishmentsWrapper}>
                <Box className={classes.sectionHeader}>
                    <Typography className={classes.sectionTitle}>
                        <StorefrontIcon style={{ fontSize: '2rem' }} />
                        Points de vente
                    </Typography>
                    <Chip 
                        label={`${establishments.length} ${establishments.length > 1 ? 'lieux' : 'lieu'}`}
                        className={classes.chip}
                    />
                </Box>
                {establishments.length === 0 ? (
                    <Box className={classes.emptyState}>
                        <StorefrontIcon style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }} />
                        <Typography>Aucun point de vente trouvé.</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {establishments.map((establishment, index) => (
                            <Grid item xs={12} sm={6} md={4} key={establishment.id}>
                                <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                                    <Card className={`${classes.card} ${classes.establishmentCard}`}>
                                        <CardContent className={classes.cardContent}>
                                            <Typography className={classes.establishmentTitle}>
                                                {establishment.name}
                                            </Typography>
                                            <Typography className={classes.establishmentText}>
                                                {establishment.address}
                                            </Typography>
                                            <Typography className={classes.establishmentText}>
                                                {establishment.city}
                                            </Typography>
                                        </CardContent>
                                        <CardActions style={{ padding: '0 24px 24px' }}>
                                            <Button
                                                variant="outlined"
                                                className={classes.manageButton}
                                                component={RouterLink}
                                                to={`/admin/manage-shop/${establishment.id}`}
                                                fullWidth
                                            >
                                                Gérer les stocks →
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Zoom>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </div>

            <Divider className={classes.divider} />

            <Box px={4} py={4} position="relative" zIndex={1}>
                <Box className={classes.sectionHeader}>
                    <Typography className={classes.sectionTitle}>
                        <LocalOfferIcon style={{ fontSize: '2rem' }} />
                        Produits boutique
                    </Typography>
                    <Chip 
                        label={`${containerCards.length} ${containerCards.length > 1 ? 'produits' : 'produit'}`}
                        className={classes.chip}
                    />
                </Box>
                {containerCards.length === 0 ? (
                    <Box className={classes.emptyState}>
                        <LocalOfferIcon style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }} />
                        <Typography>Aucun produit n'est encore publié.</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {containerCards.map((containerCard, index) => (
                            <Grid item xs={12} sm={6} md={4} key={containerCard.id}>
                                <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                                    <Card className={`${classes.card} ${classes.productCard}`}>
                                        <CardMedia
                                            className={classes.productMedia}
                                            image={containerCard.image_url
                                                ? `https://backend.csibon.app/static/container_images/${containerCard.image_url}`
                                                : 'https://placehold.co/320x240?text=Image'}
                                            title={containerCard.name}
                                        />
                                        <div className={classes.productDetails}>
                                            <Typography className={classes.productTitle}>
                                                {containerCard.name}
                                            </Typography>
                                            <Typography className={classes.productDescription}>
                                                {containerCard.description}
                                            </Typography>
                                            <Chip
                                                label={`${containerCard.price} €`}
                                                className={classes.priceChip}
                                                icon={<LocalOfferIcon style={{ color: '#fbbf24' }} />}
                                            />
                                            {containerCard.container_deposit !== undefined && containerCard.container_deposit !== null && containerCard.container_deposit !== '' && (
                                                <Chip
                                                    label={`Consigne ${containerCard.container_deposit} € x ${containerCard.container_quantity || 1}`}
                                                    className={classes.priceChip}
                                                    style={{ backgroundColor: '#ecfeff', color: '#0ea5e9', borderColor: '#67e8f9' }}
                                                />
                                            )}
                                            {containerCard.container_name && (
                                                <Typography variant="body2" style={{ color: '#475569', marginTop: 8 }}>
                                                    Contenant : {containerCard.container_name}
                                                </Typography>
                                            )}
                                        </div>
                                        <div className={classes.cardActions}>
                                            <Button
                                                variant="contained"
                                                className={classes.editButton}
                                                startIcon={<CreateIcon />}
                                                onClick={() => handleUpdateClick(containerCard)}
                                            >
                                                Modifier
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                className={classes.deleteButton}
                                                startIcon={<DeleteIcon />}
                                                onClick={() => handleDeleteClick(containerCard.id)}
                                            >
                                                Supprimer
                                            </Button>
                                        </div>
                                    </Card>
                                </Zoom>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            <AddContainerCardModal
                open={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                onAdd={handleAddContainerCard}
                establishments={establishments}
                containers={availableContainers}
            />
            <UpdateContainerCardModal
                open={updateModalOpen}
                onClose={() => setUpdateModalOpen(false)}
                onUpdate={handleUpdateContainerCard}
                establishments={establishments}
                containerCard={selectedContainerCard}
                containers={availableContainers}
            />
            <DeleteConfirmationModal
                open={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setContainerCardToDelete(null);
                }}
                onConfirm={handleDeleteConfirmClick}
                containerCardId={containerCardToDelete}
            />
        </div>
    );
}

function UpdateContainerCardModal({ open, onClose, onUpdate, containerCard, establishments, containers }) {
    const classes = useStyles();
    const [name, setName] = useState(containerCard ? containerCard.name : '');
    const [description, setDescription] = useState(containerCard ? containerCard.description : '');
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(containerCard ? containerCard.image_url : '');
    const [price, setPrice] = useState(containerCard ? containerCard.price : '');
    const [containerDeposit, setContainerDeposit] = useState(containerCard ? containerCard.container_deposit ?? '' : '');
    const [containerQuantity, setContainerQuantity] = useState(containerCard ? containerCard.container_quantity ?? 1 : 1);
    const [selectedContainerId, setSelectedContainerId] = useState(containerCard ? containerCard.container_id || '' : '');
    const [selectedEstablishments, setSelectedEstablishments] = useState([]);
    const [defaultStock, setDefaultStock] = useState(0);
    const [deliveryThreshold, setDeliveryThreshold] = useState(0);
    const [progressEnabled, setProgressEnabled] = useState(true);

    useEffect(() => {
        if (containerCard) {
            setName(containerCard.name);
            setDescription(containerCard.description);
            setImage(null);
            setImagePreview(containerCard.image_url);
            setPrice(containerCard.price ?? '');
            setContainerDeposit(containerCard.container_deposit ?? '');
            setContainerQuantity(containerCard.container_quantity ?? 1);
            setSelectedContainerId(containerCard.container_id || '');
            setSelectedEstablishments([]);
            setDefaultStock(0);
            setDeliveryThreshold(0);
            setProgressEnabled(true);
        }
    }, [containerCard]);

    if (!containerCard) {
        return null;
    }

    const handleUpdateClick = () => {
        let updatedContainerCard = {
            ...containerCard,
            name,
            description,
            price,
            container_deposit: containerDeposit,
            container_quantity: containerQuantity,
            container_id: selectedContainerId
        };
        if (image) {
            updatedContainerCard.image = image;
        }
        onUpdate(updatedContainerCard, {
            establishmentIds: selectedEstablishments,
            defaultStock,
            deliveryThreshold,
            progressEnabled
        });
    };

    return (
        <Modal 
            open={open} 
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <Zoom in={open}>
                <div className={classes.modal}>
                    <Typography className={classes.modalTitle}>
                        Modifier la fiche
                    </Typography>

                    <input
                        type="file"
                        accept="image/*"
                        id="update-file-input"
                        className={classes.fileInput}
                        onChange={e => {
                            const file = e.target.files[0];
                            setImage(file || null);
                            setImagePreview(file ? URL.createObjectURL(file) : containerCard.image_url);
                        }}
                    />
                    <label htmlFor="update-file-input" className={classes.fileInputLabel}>
                        <Typography className={classes.fileInputText}>
                            📸 Cliquez pour choisir une image
                        </Typography>
                    </label>

                    {imagePreview && (
                        <img
                            src={imagePreview.startsWith('blob:') ? imagePreview : `https://backend.csibon.app/static/container_images/${imagePreview}`}
                            alt={name}
                            className={classes.imagePreview}
                        />
                    )}

                    <TextField 
                        label="Nom du produit" 
                        value={name} 
                        onChange={e => setName(e.target.value)} 
                        fullWidth 
                        variant="outlined"
                        className={classes.textField}
                    />
                    <TextField 
                        label="Description" 
                        value={description} 
                        onChange={e => setDescription(e.target.value)} 
                        fullWidth 
                        multiline
                        rows={3}
                        variant="outlined"
                        className={classes.textField}
                    />
                    <TextField
                        label="Prix (€)"
                        value={price}
                        onChange={e => setPrice(e.target.value)}
                        fullWidth
                        type="number"
                        variant="outlined"
                        className={classes.textField}
                    />

                    <FormControl fullWidth variant="outlined" className={classes.selectField}>
                        <InputLabel>Contenant associé</InputLabel>
                        <Select
                            value={selectedContainerId}
                            onChange={e => setSelectedContainerId(e.target.value)}
                            label="Contenant associé"
                            renderValue={(value) => {
                                const selected = (containers || []).find(container => container.id === value);
                                return selected ? selected.name : 'Aucun';
                            }}
                        >
                            <MenuItem value=""><em>Aucun</em></MenuItem>
                            {(containers || []).map(container => (
                                <MenuItem key={container.id} value={container.id}>
                                    <ListItemText primary={container.name} secondary={container.volume} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Montant de consigne (€)"
                        value={containerDeposit}
                        onChange={e => setContainerDeposit(e.target.value)}
                        fullWidth
                        type="number"
                        variant="outlined"
                        className={classes.textField}
                    />

                    <TextField
                        label="Nombre de contenants par produit"
                        type="number"
                        value={containerQuantity}
                        onChange={e => setContainerQuantity(parseInt(e.target.value, 10) || 1)}
                        fullWidth
                        variant="outlined"
                        className={classes.textField}
                    />

                    <FormControl fullWidth variant="outlined" className={classes.selectField}>
                        <InputLabel>Établissements de retrait</InputLabel>
                        <Select
                            multiple
                            value={selectedEstablishments}
                            onChange={e => setSelectedEstablishments(e.target.value)}
                            label="Établissements de retrait"
                            renderValue={(selected) =>
                                (Array.isArray(establishments) ? establishments : [])
                                    .filter((establishment) => selected.includes(establishment.id))
                                    .map((establishment) => establishment.name)
                                    .join(', ')
                            }
                        >
                            {(Array.isArray(establishments) ? establishments : []).map((establishment) => (
                                <MenuItem key={establishment.id} value={establishment.id}>
                                    <Checkbox checked={selectedEstablishments.indexOf(establishment.id) > -1} />
                                    <ListItemText primary={establishment.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField 
                        label="Stock initial pour ces établissements" 
                        type="number" 
                        value={defaultStock} 
                        onChange={e => setDefaultStock(parseInt(e.target.value, 10) || 0)} 
                        fullWidth 
                        variant="outlined"
                        className={classes.textField}
                    />
                    <TextField 
                        label="Seuil de livraison groupée" 
                        type="number" 
                        value={deliveryThreshold} 
                        onChange={e => setDeliveryThreshold(parseInt(e.target.value, 10) || 0)} 
                        fullWidth 
                        variant="outlined"
                        className={classes.textField}
                    />

                    <FormControlLabel
                        className={classes.switchControl}
                        control={
                            <Switch 
                                checked={progressEnabled} 
                                onChange={e => setProgressEnabled(e.target.checked)} 
                                color="primary" 
                            />
                        }
                        label="Activer la barre de progression"
                    />

                    <Box display="flex" gap={2} mt={2}>
                        <Button 
                            variant="contained" 
                            onClick={onClose} 
                            fullWidth 
                            className={`${classes.modalButton} ${classes.cancelButton}`}
                        >
                            Annuler
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={handleUpdateClick} 
                            fullWidth 
                            className={`${classes.modalButton} ${classes.saveButton}`}
                        >
                            Mettre à jour
                        </Button>
                    </Box>
                </div>
            </Zoom>
        </Modal>
    );
}

function AddContainerCardModal({ open, onClose, onAdd, establishments, containers }) {
    const classes = useStyles();
    const availableEstablishments = Array.isArray(establishments) ? establishments : [];
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [price, setPrice] = useState('');
    const [containerDeposit, setContainerDeposit] = useState('');
    const [containerQuantity, setContainerQuantity] = useState(1);
    const [selectedContainerId, setSelectedContainerId] = useState('');
    const [selectedEstablishments, setSelectedEstablishments] = useState([]);
    const [defaultStock, setDefaultStock] = useState(0);
    const [deliveryThreshold, setDeliveryThreshold] = useState(0);
    const [progressEnabled, setProgressEnabled] = useState(true);

    useEffect(() => {
        if (open) {
            setName('');
            setDescription('');
            setImage(null);
            setImagePreview('');
            setPrice('');
            setContainerDeposit('');
            setContainerQuantity(1);
            setSelectedContainerId('');
            setSelectedEstablishments([]);
            setDefaultStock(0);
            setDeliveryThreshold(0);
            setProgressEnabled(true);
        }
    }, [open]);

    const handleSaveClick = () => {
        const containerCard = {
            id: Math.random(),
            name,
            description,
            image,
            price: price === '' ? undefined : price,
            container_deposit: containerDeposit,
            container_quantity: containerQuantity,
            container_id: selectedContainerId,
        };
        onAdd(containerCard, {
            establishmentIds: selectedEstablishments,
            defaultStock,
            deliveryThreshold,
            progressEnabled
        });
    };

    return (
        <Modal 
            open={open} 
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <Zoom in={open}>
                <div className={classes.modal}>
                    <Typography className={classes.modalTitle}>
                        Ajouter une nouvelle fiche
                    </Typography>

                    <input
                        type="file"
                        accept="image/*"
                        id="add-file-input"
                        className={classes.fileInput}
                        onChange={e => {
                            const file = e.target.files[0];
                            setImage(file || null);
                            setImagePreview(file ? URL.createObjectURL(file) : '');
                        }}
                    />
                    <label htmlFor="add-file-input" className={classes.fileInputLabel}>
                        <Typography className={classes.fileInputText}>
                            📸 Cliquez pour choisir une image
                        </Typography>
                    </label>

                    {imagePreview && (
                        <img
                            src={imagePreview}
                            alt={name || 'Aperçu du produit'}
                            className={classes.imagePreview}
                        />
                    )}

                    <TextField 
                        label="Nom du produit" 
                        value={name} 
                        onChange={e => setName(e.target.value)} 
                        fullWidth 
                        variant="outlined"
                        className={classes.textField}
                    />
                    <TextField 
                        label="Description" 
                        value={description} 
                        onChange={e => setDescription(e.target.value)} 
                        fullWidth 
                        multiline
                        rows={3}
                        variant="outlined"
                        className={classes.textField}
                    />
                    <TextField
                        label="Prix (€)"
                        value={price}
                        onChange={e => setPrice(e.target.value)}
                        fullWidth
                        type="number"
                        variant="outlined"
                        className={classes.textField}
                    />

                    <FormControl fullWidth variant="outlined" className={classes.selectField}>
                        <InputLabel>Contenant associé</InputLabel>
                        <Select
                            value={selectedContainerId}
                            onChange={e => setSelectedContainerId(e.target.value)}
                            label="Contenant associé"
                            renderValue={(value) => {
                                const selected = (containers || []).find(container => container.id === value);
                                return selected ? selected.name : 'Aucun';
                            }}
                        >
                            <MenuItem value=""><em>Aucun</em></MenuItem>
                            {(containers || []).map(container => (
                                <MenuItem key={container.id} value={container.id}>
                                    <ListItemText primary={container.name} secondary={container.volume} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Montant de consigne (€)"
                        value={containerDeposit}
                        onChange={e => setContainerDeposit(e.target.value)}
                        fullWidth
                        type="number"
                        variant="outlined"
                        className={classes.textField}
                    />

                    <TextField
                        label="Nombre de contenants par produit"
                        type="number"
                        value={containerQuantity}
                        onChange={e => setContainerQuantity(parseInt(e.target.value, 10) || 1)}
                        fullWidth
                        variant="outlined"
                        className={classes.textField}
                    />

                    <FormControl fullWidth variant="outlined" className={classes.selectField}>
                        <InputLabel>Établissements de retrait</InputLabel>
                        <Select
                            multiple
                            value={selectedEstablishments}
                            onChange={e => setSelectedEstablishments(e.target.value)}
                            label="Établissements de retrait"
                            renderValue={(selected) =>
                                availableEstablishments
                                    .filter((establishment) => selected.includes(establishment.id))
                                    .map((establishment) => establishment.name)
                                    .join(', ')
                            }
                        >
                            {availableEstablishments.map((establishment) => (
                                <MenuItem key={establishment.id} value={establishment.id}>
                                    <Checkbox checked={selectedEstablishments.indexOf(establishment.id) > -1} />
                                    <ListItemText primary={establishment.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField 
                        label="Stock initial pour ces établissements" 
                        type="number" 
                        value={defaultStock} 
                        onChange={e => setDefaultStock(parseInt(e.target.value, 10) || 0)} 
                        fullWidth 
                        variant="outlined"
                        className={classes.textField}
                    />
                    <TextField 
                        label="Seuil de livraison groupée" 
                        type="number" 
                        value={deliveryThreshold} 
                        onChange={e => setDeliveryThreshold(parseInt(e.target.value, 10) || 0)} 
                        fullWidth 
                        variant="outlined"
                        className={classes.textField}
                    />

                    <FormControlLabel
                        className={classes.switchControl}
                        control={
                            <Switch 
                                checked={progressEnabled} 
                                onChange={e => setProgressEnabled(e.target.checked)} 
                                color="primary" 
                            />
                        }
                        label="Activer la barre de progression"
                    />

                    <Box display="flex" gap={2} mt={2}>
                        <Button 
                            variant="contained" 
                            onClick={onClose} 
                            fullWidth 
                            className={`${classes.modalButton} ${classes.cancelButton}`}
                        >
                            Annuler
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={handleSaveClick} 
                            fullWidth 
                            className={`${classes.modalButton} ${classes.saveButton}`}
                        >
                            Sauvegarder
                        </Button>
                    </Box>
                </div>
            </Zoom>
        </Modal>
    );
}

export default ManageProductShop;